import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { DialogBase, DialogOptions } from '@context/frontend/common';
import { applyValidators } from '@context/frontend/form';
import { getFirebaseErrorKey } from '@context/shared/utils';
import { TranslocoModule } from '@jsverse/transloco';
import {
	TuiButton,
	TuiError,
	TuiIcon,
	TuiInput,
	TuiLabel,
	TuiNotification,
} from '@taiga-ui/core';
import { TuiButtonLoading, TuiPassword } from '@taiga-ui/kit';
import { FirebaseError } from 'firebase/app';
import { AuthService } from '../../services';

@Component({
	imports: [
		TranslocoModule,
		TuiButton,
		ReactiveFormsModule,
		FormsModule,
		TuiInput,
		TuiLabel,
		TuiButtonLoading,
		TuiIcon,
		TuiPassword,
		TuiError,
		TuiNotification,
	],
	selector: 'ctx-authentication-dialog',
	templateUrl: 'authentication-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthenticationDialogComponent
	extends applyValidators(DialogBase<boolean, { message?: string }>)
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'auth.sign-in',
		size: 's',
	} as DialogOptions;

	readonly busy = signal(false);

	form: null | FormGroup = null;

	/**
	 * the current error being displayed
	 */
	readonly error = signal<string | null>(null);

	private readonly authService = inject(AuthService);

	ngOnInit() {
		this.setupForm();
	}

	setupForm() {
		this.form = new FormGroup({
			email: new FormControl(null, [
				(field) =>
					this.getEmailValidator(field, 'form.error.email.valid'),
				(field) =>
					this.getRequiredValidator(
						field,
						'form.error.email.required',
					),
			]),
			password: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.password.required',
				),
			),
		});
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (this.busy() || !this.form || this.form?.invalid) return;

		this.form.disable();
		this.busy.set(true);

		const { email, password } = this.form.getRawValue();
		this.authService
			.reauthenticateWithEmailAndPassword(email, password)
			.then(() => this.onDismiss(true))
			.catch((error: FirebaseError) => {
				this.error.set(getFirebaseErrorKey(error.code));
				this.busy.set(false);
				this.form?.enable();
			});
	}
}
